import api from './api'

export interface Lesson {
  lesson_id: number
  course_id?: number | null
  name: string
  description?: string
  filename?: string
  material_filepath?: string
  file_type?: string
  is_overview?: boolean
  sort_order?: number
  status: number // 0 - inactive, 1 - active
  exam_id?: number | null
  added_at: string
  updated_at: string
  access_date_start?: string
  access_date_end?: string
  exam?: {
    exam_id: number
    exam_name: string
    exam_status: number
  }
  time_setting?: {
    id: number
    lesson_id: number
    start_date: string
    end_date: string
    created_at: string
    updated_at: string
  }
}

export interface CreateLessonPayload {
  course_id?: number | null
  name: string
  description?: string
  is_overview?: boolean
  sort_order?: number
  status?: number // 0 - inactive, 1 - active
  exam_id?: number | null
}

export interface CalibratePayload {
  start_month?: number // 1-12, default: 1 (January)
  start_year?: number // default: current year
  cycles?: number // 1-10, default: 3
  lesson_order?: number[] // Custom order of lesson IDs
}

export interface CalibrateResponse {
  total_lessons: number
  cycles_generated?: number
  cycles_previewed?: number
  start_month: string
  total_settings_created?: number
  total_settings_preview?: number
  settings?: Array<{
    cycle: number
    lesson_id: number
    lesson_name: string
    start_date: string
    end_date: string
    month_year: string
  }>
  preview?: Array<{
    cycle: number
    lesson_id: number
    lesson_name: string
    start_date: string
    end_date: string
    month_year: string
  }>
}

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export interface LessonRotation {
  lesson_id: number
  lesson_name: string
  start_date: string
  end_date: string
  month: number
  month_name: string
  year: number
  is_current: boolean
  is_past: boolean
  is_future: boolean
}

export class LessonsService {
  static async getLessons(): Promise<Lesson[]> {
    try {
      const response = await api.get<Lesson[]>('/lessons')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getActiveLessons(): Promise<Lesson[]> {
    try {
      const response = await api.get<Lesson[]>('/lessons/active')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getInactiveLessons(): Promise<Lesson[]> {
    try {
      const response = await api.get<Lesson[]>('/lessons/inactive')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getAvailableLessonsForExams(): Promise<{ exam_id: number; exam_name: string; exam_status: number }[]> {
    try {
      const response = await api.get<ApiResponse<{ exam_id: number; exam_name: string; exam_status: number }[]>>('/lessons/available-for-exams')
      return response.data.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async getLessonRotations(year?: number): Promise<LessonRotation[]> {
    try {
      const params = year ? { year } : {}
      const response = await api.get<ApiResponse<LessonRotation[]>>('/lessons/rotations', { params })
      return response.data.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async createLesson(payload: CreateLessonPayload): Promise<ApiResponse<Lesson>> {
    try {
      const response = await api.post<ApiResponse<Lesson>>('/lessons', payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        // For Laravel validation errors, throw the response data directly
        // This preserves the structure: { message: "...", errors: {...} }
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async updateLesson(id: number, payload: Partial<CreateLessonPayload>): Promise<ApiResponse<Lesson>> {
    try {
      const response = await api.put<ApiResponse<Lesson>>(`/lessons/${id}`, payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async deleteLesson(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`/lessons/${id}`)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async uploadLessonFile(lessonId: number, file: File): Promise<ApiResponse<{
    file_url: string
    original_filename: string
    filename: string
  }>> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<ApiResponse<{
        file_url: string
        original_filename: string
        filename: string
      }>>(`/lessons/${lessonId}/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async updateLessonStatus(id: number, status: 0 | 1): Promise<ApiResponse<{
    lesson_id: number
    status: number
    status_name: string
  }>> {
    try {
      const response = await api.put<ApiResponse<{
        lesson_id: number
        status: number
        status_name: string
      }>>(`/lessons/${id}/status`, { status })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async calibrateLessons(payload: CalibratePayload = {}): Promise<ApiResponse<CalibrateResponse>> {
    try {
      // Use 4-minute timeout for calibration sync (240000ms)
      const response = await api.post<ApiResponse<CalibrateResponse>>('/lessons/calibrate', payload, {
        timeout: 240000
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async previewCalibration(payload: CalibratePayload = {}): Promise<ApiResponse<CalibrateResponse>> {
    try {
      // Use 4-minute timeout for calibration preview (240000ms)
      const response = await api.post<ApiResponse<CalibrateResponse>>('/lessons/calibrate/preview', payload, {
        timeout: 240000
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async downloadFile(lessonId: number): Promise<Blob> {
    try {
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime()
      const response = await api.get(`/lessons/${lessonId}/download?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        // If error response is a blob, convert it to JSON
        if (error.response.data instanceof Blob) {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          throw errorData
        }
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async previewFile(lessonId: number): Promise<ApiResponse<{
    lesson_id: number
    lesson_name: string
    filename: string
    file_extension: string
    file_size: number
    file_size_formatted: string
    mime_type: string
    file_url: string
    is_previewable: boolean
    preview_type: string
  }>> {
    try {
      // Add cache-busting timestamp for file preview metadata
      const timestamp = new Date().getTime()
      const response = await api.get<ApiResponse<{
        lesson_id: number
        lesson_name: string
        filename: string
        file_extension: string
        file_size: number
        file_size_formatted: string
        mime_type: string
        file_url: string
        is_previewable: boolean
        preview_type: string
      }>>(`/lessons/${lessonId}/preview?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }
}

export default LessonsService
